import type { UseQueryOptions } from "@tanstack/react-query";

import { isApiError, useFetch } from "@/hooks/FetchHook";
import type {
  EcgStudyIndexEntry,
  EcgWaveformResponse,
  SpirometryCurvesResponse,
  SpirometryEffortsResponse,
} from "@/components/Waveforms/types";

// Waveforms are immutable once ingested — cache aggressively.
// (Cast: useFetch supplies the queryKey; only overrides belong here.)
const WAVEFORM_QUERY_OPTIONS = {
  staleTime: Infinity,
  gcTime: 10 * 60 * 1000,
} as UseQueryOptions;

/**
 * Point budget for a single ECG strip at the default 25 mm/s sweep speed.
 *
 * A 10 s record at 500 Hz is 5000 samples per lead, but the strip is only
 * ~1000 px wide at that speed and the renderer min/max-downsamples to ~2
 * points per pixel — so anything past ~2000 is transferred and parsed only to
 * be discarded before it is drawn. Use `ecgPointsForSweepSpeed` when the speed
 * is user-adjustable, so a faster (wider) sweep still gets full detail.
 */
const DEFAULT_ECG_MAX_POINTS = 2000;
/**
 * Multi-trace overlays (compare-subjects, visit trends) share one plot, so each
 * trace gets a fraction of the width — and N of them are fetched at once.
 */
export const OVERLAY_ECG_MAX_POINTS = 800;
const DEFAULT_SPIRO_MAX_POINTS = 2000;

/** Sweep speed the DEFAULT_ECG_MAX_POINTS budget was sized for (mm/s). */
const REFERENCE_SWEEP_SPEED_MM_PER_S = 25;

/** Backend's own floor for the `max_points` parameter. */
const MIN_ECG_MAX_POINTS = 50;

/**
 * Points to request for a strip drawn at `mmPerSecond`.
 *
 * Strip width scales with sweep speed, and so does the number of points the
 * renderer can actually show — a faster sweep needs proportionally more, a
 * slower one needs fewer. Keeps drawn resolution constant across the speed
 * control instead of over-fetching for the widest case.
 */
export function ecgPointsForSweepSpeed(mmPerSecond: number): number {
  const scaled = Math.round(
    (DEFAULT_ECG_MAX_POINTS * mmPerSecond) / REFERENCE_SWEEP_SPEED_MM_PER_S,
  );
  return Math.max(MIN_ECG_MAX_POINTS, scaled);
}

export const HTTP_NOT_FOUND = 404;

/** ApiError with statusCode 404 — no waveform exists / not visible. */
export function isNotFoundError(error: unknown): boolean {
  return isApiError(error) && error.statusCode === HTTP_NOT_FOUND;
}

export interface EcgWaveformQueryParams {
  imagingStudyId: string | null | undefined;
  leads?: string[];
  maxPoints?: number;
  enabled?: boolean;
}

export interface SpirometryEffortsQueryParams {
  subjectId?: string | null;
}

export interface SpirometryCurvesQueryParams {
  transmittalNumber: number | null | undefined;
  effortNumber?: number;
  maxPoints?: number;
  enabled?: boolean;
}

export const useEcgStudyIndexQuery = () => {
  const query = useFetch({
    endpointSuffix: "waveforms/ecg/studies/",
    useQueryOptions: WAVEFORM_QUERY_OPTIONS,
  });

  return { ...query, data: query.data as EcgStudyIndexEntry[] | undefined };
};

export const useEcgWaveformQuery = ({
  imagingStudyId,
  leads,
  maxPoints = DEFAULT_ECG_MAX_POINTS,
  enabled = true,
}: EcgWaveformQueryParams) => {
  const params = new URLSearchParams({
    imaging_study_id: imagingStudyId ?? "",
    max_points: String(maxPoints),
  });
  if (leads?.length) {
    params.set("leads", leads.join(","));
  }

  const query = useFetch({
    endpointSuffix: `waveforms/ecg/?${params.toString()}`,
    enabled: enabled && Boolean(imagingStudyId),
    useQueryOptions: WAVEFORM_QUERY_OPTIONS,
  });

  return { ...query, data: query.data as EcgWaveformResponse | undefined };
};

export const useSpirometryEffortsQuery = (
  opts: SpirometryEffortsQueryParams = {},
) => {
  const params = new URLSearchParams();
  if (opts.subjectId) {
    params.set("subject_id", opts.subjectId);
  }
  const queryString = params.toString();

  const query = useFetch({
    endpointSuffix: `waveforms/spirometry/efforts/${queryString ? `?${queryString}` : ""}`,
    useQueryOptions: WAVEFORM_QUERY_OPTIONS,
  });

  return {
    ...query,
    data: query.data as SpirometryEffortsResponse | undefined,
  };
};

export const useSpirometryCurvesQuery = ({
  transmittalNumber,
  effortNumber,
  maxPoints = DEFAULT_SPIRO_MAX_POINTS,
  enabled = true,
}: SpirometryCurvesQueryParams) => {
  const params = new URLSearchParams({
    transmittal_number:
      transmittalNumber != null ? String(transmittalNumber) : "",
    max_points: String(maxPoints),
  });
  if (effortNumber != null) {
    params.set("effort_number", String(effortNumber));
  }

  const query = useFetch({
    endpointSuffix: `waveforms/spirometry/curves/?${params.toString()}`,
    enabled: enabled && transmittalNumber != null,
    useQueryOptions: WAVEFORM_QUERY_OPTIONS,
  });

  return { ...query, data: query.data as SpirometryCurvesResponse | undefined };
};
